import { useEffect, useState } from "react";

import { Filter, Search } from "@carbon/icons-react";
import InfiniteScroll from "react-infinite-scroll-component";

import { Empty } from "@/assets/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { getMessage } from "@/utils/message";

import { Loader2 } from "lucide-react";
import { getDiscoverGroups } from "../../api/group.api";
import GroupCard from "../../components/GroupCard/GroupCard";
import SearchModal from "../../components/SearchModal/SearchModal";
import { GroupDef } from "../../types/group.type";

const DiscoverScreen = () => {
  const { toast } = useToast();
  const [discoverGroups, setDiscoverGroups] = useState<GroupDef[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(9);
  const [isLoadMore, setIsLoadMore] = useState(true);
  const [totalElement, setTotalElement] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchMyGroup = async (page: number) => {
    try {
      setLoading(true);
      setIsLoadMore(true);
      const result = await getDiscoverGroups({
        pageNumber: page,
        pageSize: pageSize,
      });

      setTotalElement(result.totalElements);
      setDiscoverGroups(result.data);
    } catch (error: any) {
      toast({
        title: "Thất bại!",
        variant: "error",
        description: getMessage(error.response?.data.errors[0]),
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMyGroupsScroll = async (page: number) => {
    try {
      setIsLoadMore(true);
      const result = await getDiscoverGroups({
        pageNumber: page,
        pageSize: pageSize,
      });
      if (!result.data.length || result.data.length < pageSize) {
        setIsLoadMore(false);
      }
      setDiscoverGroups(() => [...discoverGroups, ...result.data]);
    } catch (error: any) {
      toast({
        title: "Thất bại!",
        variant: "error",
        description: getMessage(error.response?.data.errors[0]),
      });
    }
  };

  useEffect(() => {
    fetchMyGroup(page);
  }, []);

  return (
    <div className="min-h-screen bg-secondary-25">
      {/* Enhanced Header with Integrated Filters */}
      <div className="bg-white sticky top-[70px] z-40">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Main Header Row */}
          <div className="py-4 border-b border-secondary-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-3">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900">
                    Khám phá nhóm
                  </h1>
                  <p className="text-sm text-secondary-600 mt-1">
                    Tìm và tham gia các nhóm phù hợp với sở thích của bạn
                  </p>
                </div>
                <Badge
                  variant="secondary"
                  className="bg-primary-50 text-primary-700 px-2 py-1 text-xs font-semibold"
                >
                  {totalElement}
                </Badge>
              </div>
              <div className="flex items-center space-x-3">
                <SearchModal />
              </div>
            </div>
          </div>

          {/* Quick Filters Row */}
          <div className="py-3">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              {/* Quick Filter Pills */}
              <div className="flex items-center gap-3">
                <div className="flex items-center space-x-2">
                  <Filter
                    size={16}
                    className="text-secondary-500"
                  />
                  <span className="text-sm font-medium text-secondary-700">
                    Danh mục:
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {/* Recommended Pills */}
                  <button className="px-3 py-1.5 rounded-full text-xs font-medium border bg-primary-500 text-white border-primary-500 shadow-sm transition-all duration-200">
                    Đề xuất
                  </button>
                  <button className="px-3 py-1.5 rounded-full text-xs font-medium border bg-white text-secondary-600 border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50 transition-all duration-200">
                    Phổ biến
                  </button>
                  <button className="px-3 py-1.5 rounded-full text-xs font-medium border bg-white text-secondary-600 border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50 transition-all duration-200">
                    Mới nhất
                  </button>
                </div>
              </div>

              {/* Search Action */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs border-secondary-200 hover:border-primary-200 hover:bg-primary-50 text-secondary-700"
                  onClick={() => {
                    // Add search functionality if needed
                  }}
                >
                  <Search
                    size={14}
                    className="mr-1"
                  />
                  Tìm kiếm nâng cao
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          </div>
        ) : (
          <>
            {/* Section Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-secondary-900">
                    Nhóm đề xuất
                  </h2>
                  <p className="text-sm text-secondary-600 mt-1">
                    Các nhóm phù hợp với bạn dựa trên sở thích và hoạt động
                  </p>
                </div>
                {totalElement > 0 && (
                  <div className="text-sm text-secondary-500">
                    Hiển thị {discoverGroups.length} / {totalElement} nhóm
                  </div>
                )}
              </div>
            </div>

            <InfiniteScroll
              style={{
                height: "unset",
                overflow: "unset",
              }}
              dataLength={discoverGroups.length}
              next={() => {
                setPage((prev) => prev + 1);
                fetchMyGroupsScroll(page + 1);
              }}
              hasMore={isLoadMore}
              loader={
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                </div>
              }
            >
              {discoverGroups.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="max-w-md">
                    <Empty />
                    <h3 className="text-xl font-semibold text-secondary-900 mt-6 mb-3">
                      Chưa có nhóm để khám phá
                    </h3>
                    <p className="text-secondary-600 mb-8 leading-relaxed">
                      Hiện tại chưa có nhóm nào để đề xuất. Hãy thử lại sau hoặc
                      tạo nhóm mới của riêng bạn.
                    </p>

                    {/* Action buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        onClick={() => {
                          // Add create group navigation
                        }}
                        className="bg-primary-600 hover:bg-primary-700 text-white"
                      >
                        Tạo nhóm mới
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          fetchMyGroup(1);
                        }}
                        className="text-primary-600 border-primary-600 hover:bg-primary-50"
                      >
                        Làm mới
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {discoverGroups.map((group) => (
                    <GroupCard
                      type="discover"
                      key={group.groupId}
                      group={group}
                    />
                  ))}
                </div>
              )}
            </InfiniteScroll>
          </>
        )}
      </div>
    </div>
  );
};

export default DiscoverScreen;
